// Masterplan-WIP-Tab: der Schlachtplan für den Ausbau des Dachportals, Schritt für Schritt
// abhakbar. Die Inhalte kommen aus om_masterplan (befüllt von masterplan_seed.mjs), hier
// wird nur gerendert und der Status umgeschaltet.
//
// Aufbau bewusst schlicht: Fortschrittsbalken oben, darunter die Säulen als Blöcke. Ein Klick
// auf die Zeile schaltet offen → läuft → erledigt → offen. Das ist schneller als ein Menü und
// verzeiht Fehlklicks, weil man einmal weiterklickt statt zu korrigieren.
import type { MarketingDeps } from "./marketing.js";

interface Schritt {
  id: number; kennung: string; saeule: string; saeule_titel: string;
  titel: string; detail: string | null; aufwand: string | null; hebel: number;
  status: "offen" | "laeuft" | "erledigt" | "verworfen"; notiz: string | null; erledigt_am: string | null;
}
interface PlanAntwort { schritte: Schritt[]; summe: { gesamt: number; erledigt: number; laeuft: number } }

const NAECHSTER: Record<string, Schritt["status"]> = {
  offen: "laeuft", laeuft: "erledigt", erledigt: "offen", verworfen: "offen",
};
const ZEICHEN: Record<string, string> = { offen: "○", laeuft: "◐", erledigt: "✓", verworfen: "—" };
const HEBEL_TEXT: Record<number, string> = { 1: "niedriger Hebel", 2: "mittlerer Hebel", 3: "hoher Hebel" };

function balken(erledigt: number, gesamt: number): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "mp-fortschritt";
  const anteil = gesamt ? Math.round((erledigt / gesamt) * 100) : 0;
  const zahl = document.createElement("div");
  zahl.className = "mp-fortschritt__zahl";
  zahl.textContent = `${erledigt} von ${gesamt} Schritten · ${anteil} %`;
  const schiene = document.createElement("div");
  schiene.className = "mp-fortschritt__schiene";
  const fuell = document.createElement("div");
  fuell.className = "mp-fortschritt__fuellung";
  fuell.style.width = `${anteil}%`;
  schiene.appendChild(fuell);
  wrap.append(zahl, schiene);
  return wrap;
}

function zeile(s: Schritt, umschalten: (s: Schritt) => void): HTMLElement {
  const row = document.createElement("div");
  row.className = `mp-schritt mp-schritt--${s.status}`;
  row.tabIndex = 0;
  row.setAttribute("role", "button");
  row.setAttribute("aria-label", `${s.titel} — Status ${s.status}, klicken zum Weiterschalten`);

  const marke = document.createElement("span");
  marke.className = "mp-schritt__marke";
  marke.textContent = ZEICHEN[s.status] ?? "\u25cb";

  const mitte = document.createElement("div");
  mitte.className = "mp-schritt__mitte";
  const t = document.createElement("div");
  t.className = "mp-schritt__titel";
  t.textContent = s.titel;
  mitte.appendChild(t);
  if (s.detail) {
    const d = document.createElement("div");
    d.className = "mp-schritt__detail";
    d.textContent = s.detail;
    mitte.appendChild(d);
  }

  const rechts = document.createElement("div");
  rechts.className = "mp-schritt__rechts";
  if (s.hebel === 3) {
    const h = document.createElement("span");
    h.className = "mp-marker mp-marker--hebel";
    h.textContent = "Hebel";
    h.title = HEBEL_TEXT[s.hebel] ?? "";
    rechts.appendChild(h);
  }
  if (s.aufwand) {
    const a = document.createElement("span");
    a.className = "mp-marker";
    a.textContent = s.aufwand;
    const AUFWAND_TEXT: Record<string, string> = { S: "kleiner Aufwand", M: "mittlerer Aufwand", L: "großer Aufwand" };
    a.title = AUFWAND_TEXT[s.aufwand] ?? "";
    rechts.appendChild(a);
  }

  row.append(marke, mitte, rechts);
  const klick = (): void => umschalten(s);
  row.addEventListener("click", klick);
  row.addEventListener("keydown", (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); klick(); } });
  return row;
}

// Seit 28.09.2026 rendert dieselbe Mechanik mehrere Pläne (Uli: Pflichtenheft FASZ und
// Baustellen-Liste „wie beim Masterplan“). Je Reiter nur die Adressen verschieden.
interface PlanReiter { tab: string; inhalt: string; neuLaden: string; name: string; url: string; statusUrl: string; leer: string }

export function initMasterplanTab(modal: HTMLElement, deps: MarketingDeps): void {
  initPlanReiter(modal, deps, { tab: "masterplan", inhalt: "#mp-inhalt", neuLaden: "#mp-neu-laden", name: "Masterplan",
    url: "/api/marketing/masterplan", statusUrl: "/api/marketing/masterplan/status", leer: "Noch keine Schritte hinterlegt — masterplan_seed.mjs ausführen." });
  initPlanReiter(modal, deps, { tab: "pflichtenheft-fasz", inhalt: "#fz-inhalt", neuLaden: "#fz-neu-laden", name: "Pflichtenheft",
    url: "/api/marketing/plan?plan=fasz", statusUrl: "/api/marketing/plan/status", leer: "Noch keine Schritte hinterlegt — plaene_seed.mjs ausführen." });
  initPlanReiter(modal, deps, { tab: "baustellen", inhalt: "#bs-inhalt", neuLaden: "#bs-neu-laden", name: "Baustellen-Liste",
    url: "/api/marketing/plan?plan=baustellen", statusUrl: "/api/marketing/plan/status", leer: "Noch keine Schritte hinterlegt — plaene_seed.mjs ausführen." });
  // Pflichten & Nachweise (Uli 12.10.2026) — gleiches Gerüst, eigener Plan „pflichten“ (pflichten_seed.mjs)
  initPlanReiter(modal, deps, { tab: "pflichten", inhalt: "#pf-inhalt", neuLaden: "#pf-neu-laden", name: "Pflichten & Nachweise",
    url: "/api/marketing/plan?plan=pflichten", statusUrl: "/api/marketing/plan/status", leer: "Noch keine Pflichten hinterlegt — pflichten_seed.mjs ausführen." });
}

function initPlanReiter(modal: HTMLElement, deps: MarketingDeps, cfg: PlanReiter): void {
  const panel = modal.querySelector<HTMLElement>(`[data-tab-panel="${cfg.tab}"]`);
  if (!panel) return;
  const host = panel.querySelector<HTMLElement>(cfg.inhalt);
  if (!host) return;

  async function laden(): Promise<void> {
    host!.textContent = `Lade ${cfg.name} …`;
    const r = await deps.api<PlanAntwort>(cfg.url);
    if (r.status !== 200 || !r.data) {
      host!.textContent = `${cfg.name} konnte nicht geladen werden.`;
      return;
    }
    const d = r.data;
    host!.textContent = "";
    if (!d.schritte.length) {
      host!.textContent = cfg.leer;
      return;
    }
    host!.appendChild(balken(d.summe.erledigt, d.summe.gesamt));

    const umschalten = async (s: Schritt): Promise<void> => {
      const neu: Schritt["status"] = NAECHSTER[s.status] ?? "laeuft";
      const antwort = await deps.api(cfg.statusUrl, "POST", { kennung: s.kennung, status: neu });
      if (antwort.status === 200) await laden();
    };

    // Nach Säule gruppieren — die Reihenfolge kommt schon sortiert aus der Datenbank.
    let aktuelle = "";
    let block: HTMLElement | null = null;
    for (const s of d.schritte) {
      if (s.saeule !== aktuelle) {
        aktuelle = s.saeule;
        const gruppe = d.schritte.filter((x) => x.saeule === s.saeule);
        const fertig = gruppe.filter((x) => x.status === "erledigt").length;
        const kopf = document.createElement("h4");
        kopf.className = "mp-saeule";
        kopf.textContent = `${s.saeule} · ${s.saeule_titel}`;
        const zahl = document.createElement("span");
        // Eine ganz abgearbeitete Säule bekommt einen grünen Zähler — dann sieht man den
        // Fortschritt schon an den Überschriften, ohne jede Zeile zu lesen.
        const komplett = fertig === gruppe.length;
        zahl.className = komplett ? "mp-saeule__zahl mp-saeule__zahl--fertig" : "mp-saeule__zahl";
        zahl.textContent = komplett ? `${fertig}/${gruppe.length} ✓` : `${fertig}/${gruppe.length}`;
        kopf.appendChild(zahl);
        block = document.createElement("div");
        block.className = "mp-block";
        host!.append(kopf, block);
      }
      block?.appendChild(zeile(s, (x) => { void umschalten(x); }));
    }
  }

  // Erst laden, wenn der Tab wirklich geöffnet wird — spart einen Request beim Modal-Start.
  const btn = modal.querySelector<HTMLButtonElement>(`[data-tab="${cfg.tab}"]`);
  let geladen = false;
  btn?.addEventListener("click", () => { if (!geladen) { geladen = true; void laden(); } });
  panel.querySelector<HTMLButtonElement>(cfg.neuLaden)?.addEventListener("click", () => { void laden(); });
}
